import { chatsService } from "services";

export const isTempTimeRegistration = (timeOrId) => {
	if (timeOrId == null) return false;
	const id = typeof timeOrId === "object" ? timeOrId.id : timeOrId;
	if (id == null) return false;
	return String(id).includes("temp");
};

export const getTimeChatId = (time) => {
	if (!time) return null;
	return time.chat_id ?? time.chatId ?? null;
};

export const hasTimeChat = (time) => Boolean(getTimeChatId(time));

export const hasTimeChatOrAttachments = (time) => {
	const hasAttachments =
		Array.isArray(time?.attachments) && time.attachments.length > 0;
	return hasTimeChat(time) || hasAttachments;
};

export const getEmployeeDisplayName = (employee) => {
	if (!employee) return "";
	return [employee.name, employee.surname].filter(Boolean).join(" ").trim();
};

export const getChatMessages = (chat) => {
	if (!chat) return [];

	const candidates = [
		chat.messages,
		chat.data?.messages,
		chat.chat?.messages,
		chat.chat_messages,
		chat.chatMessages,
	];

	for (const candidate of candidates) {
		if (Array.isArray(candidate)) return candidate;
	}

	return [];
};

export const decodeHtmlEntities = (text) => {
	if (!text || typeof text !== "string") return "";
	if (!text.includes("&")) return text;

	if (typeof document !== "undefined") {
		const textarea = document.createElement("textarea");
		textarea.innerHTML = text;
		return textarea.value;
	}

	return text
		.replace(/&#x([0-9a-fA-F]+);/g, (_, hex) => {
			const codePoint = parseInt(hex, 16);
			return Number.isFinite(codePoint) ? String.fromCodePoint(codePoint) : _;
		})
		.replace(/&#(\d+);/g, (_, num) => {
			const codePoint = parseInt(num, 10);
			return Number.isFinite(codePoint) ? String.fromCodePoint(codePoint) : _;
		})
		.replace(/&amp;/g, "&")
		.replace(/&lt;/g, "<")
		.replace(/&gt;/g, ">")
		.replace(/&quot;/g, '"')
		.replace(/&apos;/g, "'");
};

export const getMessageText = (message) =>
	decodeHtmlEntities(message?.message ?? message?.text ?? message?.body ?? "");

export const getMessageAuthorId = (message) =>
	message?.employee_id ??
	message?.employeeId ??
	message?.author_id ??
	message?.authorId ??
	message?.employee?.id ??
	message?.author?.id ??
	null;

export const getMessageCreatedAt = (message) =>
	message?.created ?? message?.created_at ?? message?.createdAt ?? null;

export const getMessageIsNote = (message) =>
	message?.is_note ?? message?.isNote ?? false;

export const getAttachmentAuthorId = (attachment) => {
	if (!attachment) return null;

	// No usar employee/employee_id: en fichajes suele ser el titular del registro, no quien subió el archivo.
	return (
		attachment.created_by_employee_id ??
		attachment.createdByEmployeeId ??
		attachment.created_by?.id ??
		attachment.createdBy?.id ??
		attachment.uploader_id ??
		attachment.uploaderId ??
		attachment.uploaded_by_employee_id ??
		attachment.uploadedByEmployeeId ??
		attachment.uploaded_by?.id ??
		attachment.uploadedBy?.id ??
		attachment.author_id ??
		attachment.authorId ??
		attachment.author?.id ??
		null
	);
};

export const getAttachmentAuthor = (attachment) => {
	if (!attachment) return null;
	if (attachment.created_by) return attachment.created_by;
	if (attachment.createdBy) return attachment.createdBy;
	if (attachment.uploader) return attachment.uploader;
	if (attachment.uploaded_by) return attachment.uploaded_by;
	if (attachment.uploadedBy) return attachment.uploadedBy;
	if (attachment.author) return attachment.author;
	return null;
};

export const getAttachmentCreatedAt = (attachment) =>
	attachment?.created ??
	attachment?.created_at ??
	attachment?.createdAt ??
	attachment?.uploaded_at ??
	null;

const attachmentAuthorsStorageKey = (timeId) =>
	`plain:time-chat-attachment-authors:${timeId}`;

export const loadStoredAttachmentAuthors = (timeId) => {
	if (!timeId) return {};

	try {
		const raw = sessionStorage.getItem(attachmentAuthorsStorageKey(timeId));
		if (!raw) return {};

		const parsed = JSON.parse(raw);
		return parsed && typeof parsed === "object" ? parsed : {};
	} catch {
		return {};
	}
};

export const saveStoredAttachmentAuthors = (timeId, authors) => {
	if (!timeId) return;

	try {
		sessionStorage.setItem(
			attachmentAuthorsStorageKey(timeId),
			JSON.stringify(authors)
		);
	} catch {
		// ignore storage errors
	}
};

export const mergeStoredAttachmentAuthors = (timeId, updates) => {
	const current = loadStoredAttachmentAuthors(timeId);
	const next = { ...current, ...updates };
	saveStoredAttachmentAuthors(timeId, next);
	return next;
};

export const extractAttachmentsFromResponse = (response) => {
	if (!response) return [];
	if (Array.isArray(response)) return response;
	if (Array.isArray(response.attachments)) return response.attachments;
	if (Array.isArray(response.data)) return response.data;
	if (Array.isArray(response.data?.attachments)) return response.data.attachments;
	return [];
};

export const normalizeChatResponse = (response) => {
	if (!response || !response.ok) return response;

	const chat =
		response.id != null
			? response
			: response.data ?? response.chat ?? response;
	const messages = getChatMessages(chat).length
		? getChatMessages(chat)
		: getChatMessages(response);

	return {
		...response,
		...chat,
		id: chat.id ?? response.id,
		messages,
	};
};

export const sendTimeRegistrationChatMessage = async (
	timeRegistrationId,
	message,
	chatId,
	isNote = false
) => {
	const trimmed = typeof message === "string" ? message.trim() : "";
	if (!trimmed || !timeRegistrationId || isTempTimeRegistration(timeRegistrationId)) {
		return null;
	}

	if (chatId) {
		return chatsService.addMessage(chatId, {
			message: trimmed,
			is_note: isNote,
		});
	}

	return chatsService.createForTimeRegistration(timeRegistrationId, {
		message: trimmed,
		is_note: isNote,
	});
};
